import React, { useState, useEffect } from 'react';
import MetaTags from '../components/SEO/MetaTags';
import {
  MapPinIcon,
  PhoneIcon,
  EnvelopeIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import {
  FaWhatsapp,
  FaLinkedin,
  FaFacebook,
} from 'react-icons/fa';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState('');

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate');
        }
      });
    }, observerOptions);

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('');

    try {
      // Simulate form submission - replace with actual API call
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Here you would typically send the data to your backend
      // For now, we'll simulate success
      setSubmitStatus('success');
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: '',
      });

      // In production, redirect to thank you page or show confirmation
      setTimeout(() => setSubmitStatus(''), 5000);
    } catch (error) {
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(''), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPinIcon,
      title: 'Address',
      content: '2nd floor, Span building, Opp. Kendra Vidyalaya, AK Road, Malappuram, Kerala 676505',
    },
    {
      icon: PhoneIcon,
      title: 'Phone',
      content: '+91 90742 86763, +91 85920 50652',
      link: 'tel:+919074286763',
    },
    {
      icon: EnvelopeIcon,
      title: 'Email',
      content: 'info@naqd.in',
      link: 'mailto:info@naqd.in',
    },
    {
      icon: ClockIcon,
      title: 'Working Hours',
      content: 'Monday - Saturday: 9:30 AM - 6:00 PM',
    },
  ];

  const services = [
    'Tax Planning & Filing',
    'GST Compliance',
    'Business Registration',
    'Audit & Assurance',
    'NRI Tax Services',
    'Financial Consulting',
  ];

  return (
    <div>
      <MetaTags
        title="Contact NAQD Consulting - Chartered Accountants in Malappuram"
        description="Get in touch with NAQD Consulting for expert chartered accountancy services in Malappuram. Contact us for tax planning, GST compliance, business registration, and more."
        keywords="contact chartered accountant malappuram, CA firm kerala contact, tax consultant malappuram phone, business registration consultation"
      />

      {/* Hero Section */}
      <section
        className="section-padding relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: "url('/assets/images/backgrounds/consulting-hero.png')" }}
      >
        <div className="absolute inset-0 bg-naqd-primary/80"></div>
        <div className="container-custom text-center relative z-10">
          <div className="animate-on-scroll">
            <h1 className="heading-primary mb-6 text-white">
              Get in <span className="text-naqd-green">Touch</span>
            </h1>
            <p className="text-xl text-white/90 dark:text-gray-300 max-w-4xl mx-auto">
              Have questions? We're here to help. Reach out to us for expert
              financial advice and support.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Information */}
            <div className="lg:col-span-1">
              <div className="space-y-6 animate-on-scroll">
                {contactInfo.map((item, index) => (
                  <div key={index} className="card">
                    <div className="flex items-start space-x-4">
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-naqd-green/10 rounded-lg flex items-center justify-center">
                          <item.icon className="w-6 h-6 text-naqd-green" />
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1">{item.title}</h3>
                        {item.link ? (
                          <a
                            href={item.link}
                            className="text-sm text-gray-300 hover:text-naqd-green transition-colors"
                          >
                            {item.content}
                          </a>
                        ) : (
                          <p className="text-sm text-gray-300">{item.content}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Quick Contact */}
              <div className="mt-8 animate-on-scroll">
                <h3 className="text-lg font-semibold mb-4">Quick Contact</h3>
                <div className="space-y-3">
                  <a
                    href="https://wa.me/919074286763"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-3 p-3 bg-white/5 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    <FaWhatsapp className="w-5 h-5 text-green-500" />
                    <div>
                      <div className="font-medium">WhatsApp</div>
                      <div className="text-sm text-gray-400">+91 90742 86763</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="card animate-on-scroll">
                <h2 className="text-2xl font-semibold mb-6">Send us a Message</h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:border-naqd-green focus:outline-none"
                        placeholder="Your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:border-naqd-green focus:outline-none"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium mb-2">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:border-naqd-green focus:outline-none"
                        placeholder="+91 9876543210"
                      />
                    </div>
                    <div>
                      <label htmlFor="service" className="block text-sm font-medium mb-2">
                        Service Needed
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:border-naqd-green focus:outline-none"
                      >
                        <option value="">Select a service</option>
                        {services.map((service) => (
                          <option key={service} value={service}>
                            {service}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium mb-2">
                      Message
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg focus:border-naqd-green focus:outline-none"
                      placeholder="Tell us about your requirements..."
                    />
                  </div>

                  {submitStatus && (
                    <div
                      className={`p-4 rounded-lg ${submitStatus === 'success'
                        ? 'bg-green-500/20 text-green-300'
                        : 'bg-red-500/20 text-red-300'
                        }`}
                    >
                      {submitStatus === 'success'
                        ? 'Thank you! We\'ll get back to you within 24 hours.'
                        : 'Something went wrong. Please try again.'}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full md:w-auto"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Google Map Section */}
      <section className="section-padding bg-naqd-dark-gray/50">
        <div className="container-custom">
          <div className="text-center mb-8 animate-on-scroll">
            <h2 className="text-2xl font-bold mb-4">Find Us</h2>
            <p className="text-gray-300">Visit our office in Malappuram</p>
          </div>

          <div className="animate-on-scroll">
            <div className="bg-gray-700 rounded-lg overflow-hidden h-96">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3918.4820335170614!2d76.0699!3d11.0414!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTEwMMKwMDInMjkuMCJOIDc2wrAwNCcxMS42IkU!5e0!3m2!1sen!2sin!4v1620000000000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="NAQD Consulting Location"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
            <div className="card text-center animate-on-scroll">
              <h3 className="text-lg font-semibold mb-2">Location</h3>
              <p className="text-sm text-gray-300">
                Centrally located in Malappuram with easy access to all major areas
              </p>
            </div>
            <div className="card text-center animate-on-scroll">
              <h3 className="text-lg font-semibold mb-2">Parking</h3>
              <p className="text-sm text-gray-300">
                Ample parking available near the office
              </p>
            </div>
            <div className="card text-center animate-on-scroll">
              <h3 className="text-lg font-semibold mb-2">Public Transport</h3>
              <p className="text-sm text-gray-300">
                Connected by bus and auto services
              </p>
            </div>
            <div className="card text-center animate-on-scroll">
              <h3 className="text-lg font-semibold mb-2">Nearby Landmarks</h3>
              <p className="text-sm text-gray-300">
                Opposite Kendra Vidyalaya, near Malappuram Town
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Emergency Contact */}
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center">
            <div className="max-w-2xl mx-auto animate-on-scroll">
              <h2 className="text-2xl font-bold mb-4">Need Immediate Assistance?</h2>
              <p className="text-gray-300 mb-6">
                For urgent tax matters or compliance issues, reach out to us immediately.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:+919074286763"
                  className="btn-primary text-lg px-8 py-4"
                >
                  Call: +91 90742 86763
                </a>
                <a
                  href="https://wa.me/919074286763"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-secondary text-lg px-8 py-4 flex items-center justify-center"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;